import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, switchMap } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import jwt_decode from 'jwt-decode';
import { AppConfigService } from './app-config.service';
import { DecodedToken } from '../models/decoded-token';
import { InsightsUtility } from '../utilities/insights/insights.utility';
import { LoginAttemptEvent } from '../insights/events/login-attempt.event';
import { AuthTokenRefreshEvent } from '../insights/events/auth-token-refresh.event';
import { LogoutAttemptEvent } from '../insights/events/logout-attempt.event';
import { Store } from '@ngrx/store';
import { UserConfigsActions } from "@store/app.actions";

const TOKEN = 'token';
const MfaProvidersKey = 'mfaProviders';

@Injectable({
	providedIn: 'root'
})
export class AuthenticationService {
	public decodedToken$: Observable<DecodedToken>;
	public supportedMfaProviders: string[];

	private token$ = new ReplaySubject<string>(1);
	private readonly url: string;
	private mfaMethod: 'Email';

	private get token(): string {
		return localStorage.getItem(TOKEN);
	}

	private set token(token: string) {
		localStorage.setItem(TOKEN, token);
	}

	constructor(private http: HttpClient, private appConfigService: AppConfigService, private insightsUtility: InsightsUtility, private store: Store) {
		if (localStorage.getItem(MfaProvidersKey)) {
			this.supportedMfaProviders = JSON.parse(localStorage.getItem(MfaProvidersKey));
		}
		this.decodedToken$ = this.token$.pipe(map((token) => jwt_decode<DecodedToken>(token)));
		this.url = `${this.appConfigService.getConfig().webApi}/api2/authentication/`;
		if (this.isLoggedIn()) {
			this.store.dispatch(UserConfigsActions.authenticated());
			this.setToken(this.token);
		}
		this.keepTokenRefreshed();

		window.addEventListener('storage', (e) => {
			if (e.key === 'token') {
				this.token$.next(e.newValue);
			}
		});
	}

	public login(username: string, password: string): Observable<DecodedToken> {
		this.deleteToken();
		return this.http.post<{ token: string, providers: string[] }>(this.url + 'tokens', {
			username,
			password
		}).pipe(
			tap(({ token, providers }) => {
				if (!token) {
					throw new Error('Login Failure');
				}
				this.setToken(token);
				if (providers) {
					localStorage.setItem(MfaProvidersKey, JSON.stringify(providers));
					this.supportedMfaProviders = [].concat(providers);
				}
				this.insightsUtility.event(new LoginAttemptEvent());
			}),
			switchMap(() => this.decodedToken$),
			take(1)
		);
	}

	public refreshToken(): Observable<string> {
		const get$ = this.http.get<{ token: string }>(this.url + 'tokens');
		return get$.pipe(
			tap(({ token }) => {
				this.setToken(token);
				this.insightsUtility.event(new AuthTokenRefreshEvent());
			}),
			map(({ token }) => token)
		);
	}

	public logout(): Observable<void> {
		const get$ = this.http.get(this.url + 'logout');
		return get$.pipe(
			map(() => {
				this.deleteToken();
				this.insightsUtility.event(new LogoutAttemptEvent());
			})
		);
	}

	public changePassword(password: string, newPassword: string): Observable<any> {
		const put$ = this.http.put(this.url + 'password', {
			userId: this.getUserId(),
			oldPassword: password,
			newPassword: newPassword
		});
		return put$;
	}

	public resetPasswordUsingCode(code: string, confirmationCode: string, userId: string, newPassword: string): Observable<boolean> {
		return this.http.put<boolean>(`${this.url}password`, {
			userId,
			resetToken: code,
			newPassword,
			confirmationCode
		});
	}

	public forgotPassword(email: string): Observable<void> {
		return this.http.post(this.url + 'forgot-password', {
			email
		}).pipe(map(() => null));
	}

	public isLoggedIn(): boolean {
		if (!this.token)
			return false;

		const decodedToken = jwt_decode<DecodedToken>(this.token);
		const now = new Date().getTime() / 1000;
		return now < decodedToken.exp;
	}

	public shouldSignEula(): boolean {
		const { rol } = jwt_decode<DecodedToken>(this.token);
		const roles = [].concat(rol);
		return roles.includes('SignEula');
	}

	public getToken$(): Observable<string> {
		return this.token$.asObservable();
	}

	public getDecodedToken$(): Observable<DecodedToken> {
		return this.getToken$().pipe(map(t => jwt_decode<DecodedToken>(t)));
	}

	public getUserId() {
		if (!this.isLoggedIn())
			return null;
		else {
			const decodedToken = jwt_decode<DecodedToken>(this.token);
			return decodedToken.uid;
		}
	}

	public requestMfaCode(method: 'Email'): Observable<any> {
		this.mfaMethod = method;
		return this.http.post(`${this.appConfigService.getConfig().webApi}/api2/authentication/generate-mfa`, { provider: method });
	}

	public validateMfaCode(code: string): Observable<any> {
		return this.http.post(`${this.appConfigService.getConfig().webApi}/api2/authentication/validate-mfa`, {
			provider: this.mfaMethod,
			mfa: code
		}).pipe(
			tap((token) => this.setToken(token))
		);
	}

	public deleteToken(): void {
		localStorage.removeItem(TOKEN);
	}

	private setToken(value: string): void {
		this.token = value;
		this.token$.next(value);
	}

	private keepTokenRefreshed(): void {
		setInterval(() => {
			if (this.isTokenAboutToExpire())
				this.refreshToken().subscribe();
		}, 30000);//Check if token will expire every 30 seconds
	}

	// True if token will expire within the next minute
	private isTokenAboutToExpire(): boolean {
		if (!this.token)
			return false;
		const now = new Date().getTime() / 1000;
		const aMinuteFromNow = now + 60;
		const decodedToken = jwt_decode<DecodedToken>(this.token);
		const isAboutToExpire = decodedToken.exp < aMinuteFromNow;
		return isAboutToExpire;
	}

}
